import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileSearch, Loader2, Download, FileText, UploadCloud, Save, CheckCircle2, PiggyBank, Trash2, Lock, Info } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { UploadCard } from "@/components/sc/UploadCard";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { rowsToLines } from "@/lib/scParse";
import { downloadCSV } from "@/lib/exporters";

const money = (v) => (v < 0 ? "-$" : "$") + Math.abs(Math.round(v)).toLocaleString();
const SEV = {
  over: { bg: "#fdeaea", fg: "#c5372f" },
  under: { bg: "#fdf3e0", fg: "#b7791f" },
  ok: { bg: "#e7f6ef", fg: "#0f8a5f" },
  unmatched: { bg: "#f1f5f9", fg: "#64748b" },
  deed: { bg: "#eef2ff", fg: "#4f46e5" },
};
const PARTY = {
  Carrier: { bg: "#e0f2fe", fg: "#0369a1" },
  HWL: { bg: "#e7f6ef", fg: "#0f8a5f" },
  Government: { bg: "#fdf3e0", fg: "#b7791f" },
};

function Stat({ label, value, tone = "text-slate-900", testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-num">{label}</div>
      <div className={`text-2xl font-display font-bold mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

export function InvoiceAuditPanel({ readOnly = false }) {
  const pdfRef = useRef(null);
  const rcRef = useRef(null);
  const [mode, setMode] = useState("pdf");
  const [invoice, setInvoice] = useState(null); // { name, lines }
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [lane, setLane] = useState({ origin: "", destination: "" });
  const [parsing, setParsing] = useState(false);
  const [tolerance, setTolerance] = useState(2);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [register, setRegister] = useState(null);
  const [rateCard, setRateCard] = useState(null);
  const [rcBusy, setRcBusy] = useState(false);

  const loadRegister = async () => {
    try {
      const { data } = await api.get("/sc/variances");
      setRegister(data);
    } catch { /* ignore */ }
  };
  const loadRateCard = async () => {
    try {
      const { data } = await api.get("/sc/uploads", { params: { doctype: "ratecard" } });
      setRateCard(data[0] || null);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadRegister(); loadRateCard(); }, []);

  const onRateCardFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setRcBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/sc/ratecard/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRateCard(data.upload);
      toast.success(`Rate card imported (${data.upload.row_count} rates from "${data.upload.sheet}").`);
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "Could not read that rate card."));
    } finally {
      setRcBusy(false);
    }
  };

  const removeRateCard = async () => {
    if (!rateCard) return;
    try {
      await api.delete(`/sc/uploads/${rateCard.id}`);
      setRateCard(null);
      toast.success("Rate card removed.");
    } catch (err) { toast.error(apiError(err?.response?.data?.detail)); }
  };

  const onPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/sc/invoice/parse-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const lines = data.lines || [];
      if (!lines.length) { toast.error("No line items found in that PDF."); return; }
      setInvoice({ name: file.name, lines });
      if (data.invoice_number) setInvoiceNo(data.invoice_number);
      if (data.invoice_date) setInvoiceDate(data.invoice_date);
      if (data.origin || data.destination) setLane({ origin: data.origin || "", destination: data.destination || "" });
      toast.success(`Extracted ${lines.length} line items from invoice.`);
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "AI extraction failed."));
    } finally {
      setParsing(false);
    }
  };

  const onInvoiceSheet = ({ rows, mapping, upload }) => {
    if (!rows || !upload) { setInvoice(null); return; }
    const lines = rowsToLines(rows, mapping || {});
    setInvoice({ name: upload.filename, lines });
    const withNo = lines.find((l) => l.invoice_number);
    if (withNo) setInvoiceNo(withNo.invoice_number);
  };

  const run = async () => {
    if (!invoice?.lines?.length) { toast.error("Add an invoice first."); return; }
    setRunning(true);
    try {
      const { data } = await api.post("/sc/invoice/audit", {
        invoice_lines: invoice.lines, tolerance_pct: Number(tolerance) || 0,
        invoice_number: invoiceNo || null, invoice_date: invoiceDate || null,
        origin: lane.origin || null, destination: lane.destination || null,
      });
      setResult(data);
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  };

  const exportCsv = () => {
    if (!result?.results?.length) return;
    downloadCSV("invoice-audit",
      ["Invoice #", "Description", "Container", "Destination", "Vessel", "Qty", "Invoiced rate", "Invoiced amount", "Expected rate", "Expected amount", "Variance", "Variance %", "Status"],
      result.results.map((r) => [r.invoice_number, r.description, r.container, r.destination, r.vessel, r.quantity, r.invoiced_rate, r.invoiced_amount, r.expected_rate, r.expected_amount, r.variance, r.variance_pct, r.status]));
  };

  const save = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await api.post("/sc/audits", {
        name: `${invoiceNo ? `Inv ${invoiceNo} — ` : ""}${invoice?.name || "Invoice"} — ${new Date().toLocaleDateString()}`,
        invoice_filename: invoice?.name, ratecard_filename: result.summary.ratecard?.filename,
        summary: result.summary, lines: result.results,
      });
      toast.success("Audit saved.");
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const overLines = (result?.results || []).filter((r) => r.severity === "over");

  const recordVariances = async () => {
    if (!overLines.length) { toast.info("No over-charges to record."); return; }
    setRecording(true);
    try {
      const { data } = await api.post("/sc/variances", {
        invoice_number: invoiceNo || null,
        ratecard_filename: result.summary.ratecard?.filename,
        lines: overLines.map((r) => ({
          invoice_number: r.invoice_number, description: r.description, destination: r.destination,
          container: r.container, lane: r.lane, invoiced_amount: r.invoiced_amount,
          expected_amount: r.expected_amount, variance: r.variance, variance_pct: r.variance_pct, currency: r.currency,
        })),
      });
      toast.success(`Recorded ${data.recorded} variance line(s) to Cost Avoidance.`);
      loadRegister();
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail));
    } finally {
      setRecording(false);
    }
  };

  const delVariance = async (id) => {
    try {
      await api.delete(`/sc/variances/${id}`);
      loadRegister();
    } catch (err) { toast.error(apiError(err?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Invoice &amp; rate-card audit</h2>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Extract invoice line items (AI for PDFs) and match them to your rate card by <strong>charge description</strong> (and destination), falling back to vessel/lane, to surface over-charges and variances.
        </p>
      </div>

      {readOnly && (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-600" data-testid="invoice-readonly-banner">
          <Lock className="w-4 h-4 text-slate-400" />
          View-only access — you can review the Cost Avoidance register below. Uploading and auditing is restricted to admins.
        </div>
      )}

      {!readOnly && (
      <div className="grid gap-4 md:grid-cols-2">
        {/* Invoice source */}
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="invoice-source-card">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="font-display font-semibold text-sm text-slate-900 flex-1">1 · Invoice</h3>
            <div className="flex rounded-lg border border-slate-200 p-0.5 text-xs">
              <button onClick={() => { setMode("pdf"); setInvoice(null); }} data-testid="mode-pdf"
                className={`px-2.5 py-1 rounded-md ${mode === "pdf" ? "bg-primary text-white" : "text-slate-500"}`}>PDF</button>
              <button onClick={() => { setMode("sheet"); setInvoice(null); }} data-testid="mode-sheet"
                className={`px-2.5 py-1 rounded-md ${mode === "sheet" ? "bg-primary text-white" : "text-slate-500"}`}>CSV / Excel</button>
            </div>
          </div>

          {mode === "pdf" ? (
            invoice ? (
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-slate-800 truncate">{invoice.name}</div>
                  <div className="text-[11px] text-slate-500">{invoice.lines.length} line items</div>
                </div>
                <button onClick={() => pdfRef.current?.click()} className="text-[11px] text-primary hover:underline">Replace</button>
              </div>
            ) : (
              <button onClick={() => pdfRef.current?.click()} disabled={parsing} data-testid="upload-invoice-pdf"
                className="w-full flex flex-col items-center gap-1.5 rounded-lg border-2 border-dashed border-slate-200 hover:border-primary/50 hover:bg-slate-50 py-5 text-slate-500">
                {parsing ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                <span className="text-xs font-medium">{parsing ? "Extracting with AI…" : "Upload invoice PDF"}</span>
                <span className="text-[10px] text-slate-400">AI reads line items · .pdf</span>
              </button>
            )
          ) : (
            <UploadCard doctype="invoice" title="Invoice spreadsheet" accent="#4a3aa7" hydrateRows
              hint="Map columns for description, lane, vessel, qty, rate, amount." onUploaded={onInvoiceSheet} />
          )}
          <input ref={pdfRef} type="file" accept=".pdf" className="hidden" onChange={onPdf} />
        </div>

        {/* Rate card */}
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="ratecard-card">
          <h3 className="font-display font-semibold text-sm text-slate-900 mb-3">2 · Rate card</h3>
          {rateCard ? (
            <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-slate-800 truncate" data-testid="active-ratecard">{rateCard.filename}</div>
                <div className="text-[11px] text-slate-500">{rateCard.row_count} rates{rateCard.sheet ? ` · ${rateCard.sheet}` : ""}</div>
              </div>
              <button onClick={() => rcRef.current?.click()} className="text-[11px] text-primary hover:underline" data-testid="replace-ratecard">Replace</button>
              <button onClick={removeRateCard} className="text-slate-400 hover:text-destructive" data-testid="delete-ratecard"><Trash2 className="w-4 h-4" /></button>
            </div>
          ) : (
            <button onClick={() => rcRef.current?.click()} disabled={rcBusy} data-testid="upload-ratecard"
              className="w-full flex flex-col items-center gap-1.5 rounded-lg border-2 border-dashed border-slate-200 hover:border-primary/50 hover:bg-slate-50 py-5 text-slate-500">
              {rcBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <UploadCloud className="w-5 h-5" />}
              <span className="text-xs font-medium">{rcBusy ? "Reading rate card…" : "Upload rate card"}</span>
              <span className="text-[10px] text-slate-400">Reads contracted rates automatically · .xlsx .xls</span>
            </button>
          )}
          <input ref={rcRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onRateCardFile} />
        </div>
      </div>
      )}

      {!readOnly && (
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          Invoice #
          <input type="text" value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)}
            placeholder="e.g. INV-10023" data-testid="invoice-number-input"
            className="w-36 rounded-md border border-slate-200 px-2 py-1 text-sm font-num" />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          Origin port
          <input type="text" value={lane.origin} onChange={(e) => setLane((l) => ({ ...l, origin: e.target.value }))}
            placeholder="e.g. Bangkok" data-testid="lane-origin-input"
            className="w-32 rounded-md border border-slate-200 px-2 py-1 text-sm" />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          Destination
          <input type="text" value={lane.destination} onChange={(e) => setLane((l) => ({ ...l, destination: e.target.value }))}
            placeholder="e.g. Sydney" data-testid="lane-destination-input"
            className="w-32 rounded-md border border-slate-200 px-2 py-1 text-sm" />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          Tolerance
          <input type="number" min="0" step="0.5" value={tolerance} onChange={(e) => setTolerance(e.target.value)}
            data-testid="tolerance-input"
            className="w-16 rounded-md border border-slate-200 px-2 py-1 text-sm font-num" />%
        </label>
        <button onClick={run} disabled={running} data-testid="run-audit-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-white px-4 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSearch className="w-4 h-4" />}
          Run audit
        </button>
        {result?.results?.length > 0 && (
          <>
            {overLines.length > 0 && (
              <button onClick={recordVariances} disabled={recording} data-testid="record-variances-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-emerald-700 disabled:opacity-60">
                {recording ? <Loader2 className="w-4 h-4 animate-spin" /> : <PiggyBank className="w-4 h-4" />}
                Record {overLines.length} to Cost Avoidance
              </button>
            )}
            <button onClick={exportCsv} data-testid="export-audit-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button onClick={save} disabled={saving} data-testid="save-audit-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save audit
            </button>
          </>
        )}
      </div>
      )}

      {result && (
        <div className="space-y-6" data-testid="audit-results">
          {result.summary.no_deviations && (
            <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="no-deviations-banner">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <div>
                <div className="font-display font-semibold text-sm text-emerald-800">No deviations found</div>
                <div className="text-xs text-emerald-700">All {result.summary.lines} invoice lines matched the rate card within tolerance.</div>
              </div>
            </div>
          )}
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            <Stat testid="stat-invoiced" label="Total invoiced" value={money(result.summary.total_invoiced)} />
            <Stat testid="stat-expected" label="Expected (rate card)" value={money(result.summary.total_expected)} />
            <Stat testid="stat-variance" label="Net variance" value={money(result.summary.total_variance)}
              tone={result.summary.total_variance > 0 ? "text-red-500" : "text-emerald-600"} />
            <Stat testid="stat-flags" label="Flags"
              value={<span className="text-lg">{result.summary.overcharges} over · {result.summary.unmatched} unmatched</span>} />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr>
                    {["Description", "Ctr", "Dest", "Qty", "Inv. rate/unit", "Exp. rate/unit", "Variance (total)", "Status"].map((h) => (
                      <th key={h} className="text-left font-medium px-3 py-2.5 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-num">
                  {result.results.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-3 py-2.5 text-slate-800">
                        <div className="flex items-center gap-1.5 max-w-[240px]">
                          <span className="truncate">{r.description || "—"}</span>
                          {r.charge_info && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button type="button" data-testid={`charge-info-${i}`}
                                  className="shrink-0 text-slate-300 hover:text-slate-600 transition-colors">
                                  <Info className="w-3.5 h-3.5" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent side="top" align="start" className="w-72 p-3">
                                <div className="flex items-center gap-2 mb-1.5">
                                  {r.charge_party && (
                                    <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                                      style={{ background: (PARTY[r.charge_party] || PARTY.HWL).bg, color: (PARTY[r.charge_party] || PARTY.HWL).fg }}>
                                      {r.charge_party === "HWL" ? "HWL / Hellmann" : r.charge_party}
                                    </span>
                                  )}
                                  <span className="text-xs font-semibold text-slate-800 truncate">{r.description}</span>
                                </div>
                                <p className="text-xs text-slate-600 leading-relaxed">{r.charge_info}</p>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">{r.container || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-600">{r.destination || r.lane || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-600">{r.quantity}</td>
                      <td className="px-3 py-2.5 text-slate-700">{money(r.invoiced_rate)}</td>
                      <td className="px-3 py-2.5 text-slate-500">{r.match_type === "none" ? "—" : money(r.expected_rate)}</td>
                      <td className={`px-3 py-2.5 font-semibold ${r.variance > 0 ? "text-red-500" : r.variance < 0 ? "text-amber-600" : "text-slate-500"}`}>
                        {r.match_type === "none" ? "—" : `${money(r.variance)}${r.expected_rate ? ` (${r.variance_pct}%/unit)` : ""}`}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap"
                          style={{ background: SEV[r.severity].bg, color: SEV[r.severity].fg }}>{r.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Cost Avoidance register */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="cost-avoidance-register">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <PiggyBank className="w-5 h-5 text-emerald-600" />
          <h3 className="font-display font-semibold text-sm text-slate-900 flex-1">Cost Avoidance register</h3>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-num">Total avoided</div>
            <div className="text-xl font-display font-bold text-emerald-600 font-num" data-testid="total-cost-avoided">
              {money(register?.total_cost_avoided || 0)}
            </div>
          </div>
          <div className="text-[11px] text-slate-400 font-num pl-2">{register?.count || 0} lines · {register?.invoices || 0} invoices</div>
          {register?.items?.[0]?.recorded_at && (
            <div className="basis-full text-[11px] text-slate-400" data-testid="cost-avoidance-updated-stamp">
              Last updated {register.items[0].owner ? `by ${register.items[0].owner} ` : ""}·{" "}
              {new Date(register.items[0].recorded_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
            </div>
          )}
        </div>
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide sticky top-0">
              <tr>
                {["Invoice #", "Charge", "Dest", "Ctr", "Invoiced", "Agreed", "Avoided", "", ""].map((h, i) => (
                  <th key={i} className="text-left font-medium px-3 py-2.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-num">
              {(register?.items || []).map((v) => (
                <tr key={v.id} className="hover:bg-slate-50" data-testid={`variance-row-${v.id}`}>
                  <td className="px-3 py-2.5 font-medium text-slate-800">{v.invoice_number || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700 max-w-[220px] truncate">{v.description || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-600">{v.destination || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-600">{v.container || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700">{money(v.invoiced_amount)}</td>
                  <td className="px-3 py-2.5 text-slate-500">{money(v.expected_amount)}</td>
                  <td className="px-3 py-2.5 font-semibold text-emerald-600">{money(v.variance)}{v.variance_pct != null ? ` (${v.variance_pct}%)` : ""}</td>
                  <td className="px-3 py-2.5 text-[11px] text-slate-400">{(v.recorded_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2.5">
                    {!readOnly && (
                      <button onClick={() => delVariance(v.id)} className="text-slate-300 hover:text-destructive" data-testid={`del-variance-${v.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!(register?.items || []).length && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">
                  No variances recorded yet. Run an audit, then click “Record to Cost Avoidance” to capture over-charges here.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
